import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Layout } from "@/components/Layout";
import { IntakeLayout } from "@/components/IntakeLayout";
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import TreatmentsPage from "@/pages/TreatmentsPage";
import PackagesPage from "@/pages/PackagesPage";
import WhyAyurvedaPage from "@/pages/WhyAyurvedaPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import FaqPage from "@/pages/FaqPage";
import TestimonialsPage from "@/pages/TestimonialsPage";
import ShopPage from "@/pages/ShopPage";
import ContactPage from "@/pages/ContactPage";
import IntakePage from "@/pages/IntakePage";
import IntakeSuccessPage from "@/pages/IntakeSuccessPage";
import NotFoundPage from "@/pages/NotFoundPage";
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import AdminDraftsPage from "@/pages/admin/AdminDraftsPage";
import AdminIntakeDetailPage from "@/pages/admin/AdminIntakeDetailPage";
import { Toaster } from "@/components/ui/sonner";

/**
 * Detect whether the user is visiting the standalone intake subdomain
 * (e.g. intake.choiceayurveda.com). When that's the case, the application
 * behaves as a single-purpose intake site: `/` redirects to `/intake` and
 * the marketing layout is never rendered at the root.
 *
 * Detection rules (any one is enough):
 *  - Hostname begins with `intake.` (preferred CNAME setup)
 *  - URL query string contains `?intake=1` (useful for previewing the
 *    standalone experience without a real subdomain)
 *  - localStorage flag `choice-ay-intake-host=1`
 */
function useIsIntakeHost() {
    const [isIntake, setIsIntake] = useState(false);
    useEffect(() => {
        try {
            const host = window.location.hostname || "";
            const qs = new URLSearchParams(window.location.search);
            const ls =
                typeof localStorage !== "undefined" &&
                localStorage.getItem("choice-ay-intake-host") === "1";
            const forced = qs.get("intake") === "1" || ls;
            const matched =
                host.startsWith("intake.") || host === "intake.localhost";
            setIsIntake(Boolean(forced || matched));
        } catch (e) {
            setIsIntake(false);
        }
    }, []);
    return isIntake;
}

function App() {
    const isIntakeHost = useIsIntakeHost();

    return (
        <div className="App bg-grain">
            <AuthProvider>
                <BrowserRouter>
                    <Routes>
                        {/* Standalone intake host: every public route shows the intake.
                            The admin area is still reachable (so Riley can review intakes
                            from this domain too if she likes). */}
                        {isIntakeHost ? (
                            <>
                                <Route element={<IntakeLayout />}>
                                    <Route path="/" element={<IntakePage />} />
                                    <Route
                                        path="/intake"
                                        element={<Navigate to="/" replace />}
                                    />
                                    <Route
                                        path="/intake/success"
                                        element={<IntakeSuccessPage />}
                                    />
                                    <Route path="/success" element={<IntakeSuccessPage />} />
                                </Route>
                            </>
                        ) : (
                            <>
                                {/* Main marketing site */}
                                <Route element={<Layout />}>
                                    <Route path="/" element={<HomePage />} />
                                    <Route path="/about" element={<AboutPage />} />
                                    <Route path="/treatments" element={<TreatmentsPage />} />
                                    <Route path="/packages" element={<PackagesPage />} />
                                    <Route
                                        path="/why-ayurveda"
                                        element={<WhyAyurvedaPage />}
                                    />
                                    <Route path="/blog" element={<BlogPage />} />
                                    <Route path="/blog/:slug" element={<BlogPostPage />} />
                                    <Route path="/faq" element={<FaqPage />} />
                                    <Route
                                        path="/testimonials"
                                        element={<TestimonialsPage />}
                                    />
                                    <Route path="/shop" element={<ShopPage />} />
                                    <Route path="/contact" element={<ContactPage />} />
                                    <Route path="*" element={<NotFoundPage />} />
                                </Route>

                                {/* Standalone intake — same standalone layout as the
                                    intake subdomain so the experience is consistent
                                    however the user arrives. */}
                                <Route element={<IntakeLayout />}>
                                    <Route path="/intake" element={<IntakePage />} />
                                    <Route
                                        path="/intake/success"
                                        element={<IntakeSuccessPage />}
                                    />
                                </Route>
                            </>
                        )}

                        {/* Admin login (standalone) */}
                        <Route path="/admin/login" element={<AdminLoginPage />} />

                        {/* Admin protected area */}
                        <Route
                            path="/admin"
                            element={
                                <ProtectedRoute>
                                    <AdminLayout />
                                </ProtectedRoute>
                            }
                        >
                            <Route index element={<AdminDashboardPage />} />
                            <Route path="drafts" element={<AdminDraftsPage />} />
                            <Route
                                path="intakes/:id"
                                element={<AdminIntakeDetailPage />}
                            />
                        </Route>

                        {/* Catch-all for the intake host */}
                        {isIntakeHost && (
                            <Route path="*" element={<Navigate to="/" replace />} />
                        )}
                    </Routes>
                </BrowserRouter>
                <Toaster richColors position="top-right" />
            </AuthProvider>
        </div>
    );
}

export default App;
